import { addMilliseconds, differenceInMilliseconds, formatDistance, formatDistanceStrict } from 'date-fns';

const csharpTimespanRegex = /^((?<days>[0-9]+)(\.))?(?<hours>[0-2][0-9]):(?<mins>[0-5][0-9]):(?<seconds>[0-5][0-9])((\.)(?<millis>[0-9]+))?$/;

export function parse(value: string) {
    const match = csharpTimespanRegex.exec(value);

    if (!match || !match.groups) return new Duration(NaN);

    const days = match.groups.days ? parseInt(match.groups.days) : 0;
    const hours = match.groups.hours ? parseInt(match.groups.hours) : 0;
    const mins = match.groups.mins ? parseInt(match.groups.mins) : 0;
    const seconds = match.groups.seconds ? parseInt(match.groups.seconds) : 0;
    const millis = match.groups.millis ? parseInt(match.groups.millis) : 0;

    const valueOf = days * 24 * 60 * 60 * 1000 + hours * 60 * 60 * 1000 + mins * 60 * 1000 + seconds * 1000 + millis;

    return new Duration(valueOf);
}

export class Duration {
    public constructor(value: number) {
        this.value = value;
    }

    public valueOf(): number {
        return this.value;
    }

    public toString(): string {
        if (isNaN(this.value)) return 'Invalid Duration';

        const days = getDays(this);
        const daysString = days ? days + '.' : '';

        const hours = getHours(this);
        const hoursString = hours.toString().padStart(2, '0');

        const mins = getMinutes(this);
        const minsString = mins.toString().padStart(2, '0');

        const seconds = getSeconds(this);
        const secondsString = seconds.toString().padStart(2, '0');

        const millis = getMillis(this);
        const millisString = millis ? '.' + millis : '';

        return daysString + hoursString + ':' + minsString + ':' + secondsString + millisString;
    }

    public toJSON(): string | null {
        if (isNaN(this.value)) return null;

        return this.toString();
    }

    private readonly value: number;
}

export function getDurationToNow(value: Date) {
    return new Duration(differenceInMilliseconds(value, Date.now()));
}

export function getDuration(start: Date, end: Date) {
    return new Duration(differenceInMilliseconds(start, end));
}

// Add Functions
export function addDuration(duration: Duration, amount: Duration) {
    return new Duration(duration.valueOf() + amount.valueOf());
}

export function addMillis(duration: Duration, amount: number) {
    return new Duration(duration.valueOf() + amount);
}

export function addSeconds(duration: Duration, amount: number) {
    return new Duration(duration.valueOf() + amount * 1000);
}

export function addMinutes(duration: Duration, amount: number) {
    return new Duration(duration.valueOf() + amount * 60 * 1000);
}

export function addHours(duration: Duration, amount: number) {
    return new Duration(duration.valueOf() + amount * 60 * 60 * 1000);
}

export function addDays(duration: Duration, amount: number) {
    return new Duration(duration.valueOf() + amount * 24 * 60 * 60 * 1000);
}

export function addWeeks(duration: Duration, amount: number) {
    return new Duration(duration.valueOf() + amount * 7 * 24 * 60 * 60 * 1000);
}

// Subtract Functions
export function subDuration(duration: Duration, amount: Duration) {
    return new Duration(duration.valueOf() - amount.valueOf());
}

export function subMillis(duration: Duration, amount: number) {
    return new Duration(duration.valueOf() - amount);
}

export function subSeconds(duration: Duration, amount: number) {
    return new Duration(duration.valueOf() - amount * 1000);
}

export function subMinutes(duration: Duration, amount: number) {
    return new Duration(duration.valueOf() - amount * 60 * 1000);
}

export function subHours(duration: Duration, amount: number) {
    return new Duration(duration.valueOf() - amount * 60 * 60 * 1000);
}

export function subDays(duration: Duration, amount: number) {
    return new Duration(duration.valueOf() - amount * 24 * 60 * 60 * 1000);
}

export function subWeeks(duration: Duration, amount: number) {
    return new Duration(duration.valueOf() + amount * 7 * 24 * 60 * 60 * 1000);
}

// Get Functions
export function getMillis(duration: Duration) {
    return Math.floor(duration.valueOf() % 1000);
}

export function getSeconds(duration: Duration) {
    return Math.floor((duration.valueOf() % (60 * 1000)) / 1000);
}

export function getMinutes(duration: Duration) {
    return Math.floor((duration.valueOf() % (60 * 60 * 1000)) / 60 / 1000);
}

export function getHours(duration: Duration) {
    return Math.floor((duration.valueOf() % (24 * 60 * 60 * 1000)) / 60 / 60 / 1000);
}

export function getDays(duration: Duration) {
    return Math.floor(duration.valueOf() / 24 / 60 / 60 / 1000);
}

// Get Total Functions
export function getTotalSeconds(duration: Duration) {
    return duration.valueOf() / 1000;
}

export function getTotalMinutes(duration: Duration) {
    return getTotalSeconds(duration) / 60;
}

export function getTotalHours(duration: Duration) {
    return getTotalMinutes(duration) / 60;
}

export function getTotalDays(duration: Duration) {
    return getTotalHours(duration) / 24;
}

export function getTotalWeeks(duration: Duration) {
    return getTotalDays(duration) / 7;
}

// TODO [add|sub][Months?|Years?]

// eslint-disable-next-line
export type formatOptions = {
    includeSeconds?: boolean;
    addSuffix?: boolean;
    locale?: Locale;
};

export function formatDuration(duration: Duration, options?: formatOptions) {
    const now = new Date();

    return formatDistance(addMilliseconds(now, duration.valueOf()), now, options);
}

export function formatDurationStrict(duration: Duration, options?: formatOptions) {
    const now = new Date();

    return formatDistanceStrict(addMilliseconds(now, duration.valueOf()), now, options);
}

export function formatDurationExplicit(duration: Duration) {
    if (duration.valueOf() == 0) return '0 Seconds';

    const days = getDays(duration);
    const daysString = days ? days + ' Days' : '';

    const hours = getHours(duration);
    const hoursString = hours ? hours.toString() + ' Hours' : '';

    const mins = getMinutes(duration);
    const minsString = mins ? mins.toString() + ' Mins' : '';

    const seconds = getSeconds(duration);
    const secondsString = seconds ? seconds.toString() + ' Seconds' : '';

    return [daysString, hoursString, minsString, secondsString].filter(s => !!s).join(' ');
}
